import React from 'react';
import { Link } from 'react-router-dom';
import { Info, ShieldCheck, Palette, Globe, Sparkles } from 'lucide-react';
import ContainerPageLayout, { CodeBlock, ReqBadge, DownloadButtons } from '../components/ContainerPageLayout';
import ApiPlayground from '../components/ApiPlayground';
import LangTabs from '../components/LangTabs';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'process', label: 'HPP Process' },
  { id: 'making-request', label: 'Making The Request', children: [
    { id: 'sample-request-url', label: 'Sample Request URL' },
    { id: 'sample-request-code', label: 'PHP / C# Sample' },
    { id: 'iframe-embed', label: 'iFrame Embed Snippet' },
    { id: 'request-reference', label: 'Request Reference' },
  ]},
  { id: 'data-required', label: 'Data Required' },
  { id: 'available-form-fields', label: 'Available Form Fields' },
  { id: 'validation', label: 'Field Validation' },
  { id: 'sample-xml', label: 'Sample HPP XML' },
  { id: 'data-returned', label: 'Data Returned' },
  { id: 'expected-responses', label: 'Expected XML Documents' },
];

const REQUEST_URL_SAMPLE = `https://staging.ecomm365.com/acqapi/service.ashx?Username=SomeName&Password=SomePassword&MessageID=30dd879c-ee2f-11db-8314-0800200c9a66&APISignature=registerpayment&Data=<R>...</R>`;

const PHP_SAMPLE = `<?php
function httpPost($url, $params) //Post method
{
    $params = urldecode(http_build_query($params)); //Convert our array of params into query string, URL decode the result to prevent data corruption

    $ch = curl_init($url); //create a new cURL resource

    //set appropriate options
    curl_setopt($ch, CURLOPT_POST, 1);
    curl_setopt($ch, CURLOPT_POSTFIELDS, $params);
    curl_setopt($ch, CURLOPT_FOLLOWLOCATION, 1);
    curl_setopt($ch, CURLOPT_HEADER, 0);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, 1);

    $response = curl_exec($ch); //grab URL and pass it to the browser while assigning the response to \`$response\`

    curl_close($ch); //close cURL resource, and free up system resources

    return $response;
}

$APIURL = "https://staging.ecomm365.com/acqapi/service.ashx"; //Set API URL to eComm staging environment
$params = array(
    "APISignature" => "Registerpayment", //API Signature - the Hosted Payments Page signature
    "MessageID"    => GUID(), //A new GUID is required for every new API Call
    "Username"     => "SomeName", //API Username
    "Password"     => "SomePassword", //API Password
    "Data"         => "<R>...</R>" //Data fields required for request
);

$Response = httpPost($APIURL, $params);
$XMLDataArray = simplexml_load_string($Response);

/* Obtain information from response fields */
$ResponseR1 = (string)$XMLDataArray->R1;
$ResponseR2 = (string)$XMLDataArray->R2;
$HostedUrl  = (string)$XMLDataArray->R4; //URL to load inside the iFrame
//... etc
?>`;

const CSHARP_SAMPLE = `[HttpPost]
public IActionResult index()
{
    HttpWebRequest httpWReq = (HttpWebRequest)WebRequest.Create("https://staging.ecomm365.com/acqapi/service.ashx");
    UTF8Encoding encoding = new UTF8Encoding();

    string pData = "username=SomeName";
    pData += "&password=SomePassword";
    pData += "&messageId=" + System.Guid.NewGuid().ToString();
    pData += "&ApiSignature=Registerpayment"; //Hosted Payments Page signature
    pData += "&Data=" + WebUtility.UrlEncode(@"<R>...</R>");

    byte[] data = encoding.GetBytes(pData);
    httpWReq.Method = "POST";
    httpWReq.ContentType = "application/x-www-form-urlencoded";
    httpWReq.ContentLength = data.Length;

    byte[] byteArray = Encoding.UTF8.GetBytes(pData);
    Stream dataStream = httpWReq.GetRequestStream();
    dataStream.Write(byteArray, 0, byteArray.Length);
    dataStream.Close();

    HttpWebResponse response = (HttpWebResponse)httpWReq.GetResponse();
    string responseString = new StreamReader(response.GetResponseStream()).ReadToEnd();

    XmlDocument xDoc = new XmlDocument();
    xDoc.LoadXml(responseString);

    XmlNodeList ResponseR1 = xDoc.GetElementsByTagName("R1");
    XmlNodeList ResponseR2 = xDoc.GetElementsByTagName("R2");
    XmlNodeList HostedUrl  = xDoc.GetElementsByTagName("R4"); //URL to load inside the iFrame
    //... etc

    return View();
}`;

const IFRAME_SNIPPET = `<!-- Embed the Hosted Payments Page URL returned in R4 -->
<iframe
  src="https://www.somewebsite.com/acs?id=43DAAL4B-78CF-4000-AC6B-EBA1F1116229"
  width="100%"
  height="640"
  style="border:0; border-radius:8px;"
  allow="payment"
  title="Secure Checkout">
</iframe>`;

const HPP_XML = `<!-- Hosted Payments Page Request Demonstration -->
<R>
    <R1>43DAAL4B-78CF-4000-AC6B-EBA1F1116229</R1>
    <R3>978</R3>
    <R4>100</R4>
    <R5>Billing First Name</R5>
    <R6>Billing Middle Name</R6>
    <R7>Billing Last Name</R7>
    <R8>IDA Business and Technology Park</R8>
    <R9>Johnstown</R9>
    <R10>Navan</R10>
    <R12>Meath</R12>
    <R13>IE</R13>
    <R14>1000596</R14>
    <R15>http://domain.com/HostedPaymentsPage/Success/</R15>
    <R16>http://domain.com/HostedPaymentsPage/Failure/</R16>
    <udf>
        <I>eCOMM</I>
        <I>435</I>
    </udf>
</R>`;

const RESP_0038 = `<!-- Sample XML Document -->
<?xml version="1.0"?>
<R>
    <R1>0038</R1>
    <R2>Register Payment Received</R2>
    <R3>43DAAL4B-78CF-4000-AC6B-EBA1F1116229</R3>
    <R4>https://www.somewebsite.com/acs?id=43DAAL4B-78CF-4000-AC6B-EBA1F1116229</R4>
</R>`;

const RESP_NOTIF = `<!-- Sample Confirmation Notification, XML Document -->
<?xml version="1.0"?>
<R>
    <R1>0000</R1>
    <R2>Successful</R2>
    <R3>43DAAL4B-78CF-4000-AC6B-EBA1F1116229</R3>
    <R4>Successful</R4>
    <R11>MerchantData</R11>
    <R12>MerchantReferenceNumber</R12>

    <!-- Card Information returned, ONLY if enabled at VID level -->
    <CN>542432******0751</CN>
    <CardType>MSCP</CardType>
    <CardExpMM>12</CardExpMM>
    <CardExpYYYY>2025</CardExpYYYY>

    <udf>
        <I>eCOMM</I>
        <I>435</I>
    </udf>
</R>`;

const FORM_FIELDS = [
  { name: 'R1',  desc: 'Transaction Code. The merchant should provide a unique Transaction Code for each transaction.', req: 'Y', def: 'AN(40)' },
  { name: 'R3',  desc: 'Sale Amount Numeric Currency Code (ISO 4217). E.g. EUR = [978], GBP = [826].', req: 'Y', def: 'N(3)' },
  { name: 'R4',  desc: 'Total Transaction Amount (without decimals). E.g. €100.00 = [10000], €0.99 = [99].', req: 'Y', def: 'N(20)' },
  { name: 'R5',  desc: 'Billing First Name.', req: 'Y', def: 'AN(50)' },
  { name: 'R6',  desc: 'Billing Middle Name.', req: 'N', def: 'AN(50)' },
  { name: 'R7',  desc: 'Billing Last Name.', req: 'Y', def: 'AN(50)' },
  { name: 'R8',  desc: 'Billing Address Line 1.', req: 'Y', def: 'AN(50)' },
  { name: 'R9',  desc: 'Billing Address Line 2.', req: 'N', def: 'AN(50)' },
  { name: 'R10', desc: 'Billing City.', req: 'Y', def: 'AN(50)' },
  { name: 'R11', desc: 'Billing State/Province.', req: 'N', def: 'AN(50)' },
  { name: 'R12', desc: 'Billing Postal Code.', req: 'Y', def: 'AN(10)' },
  { name: 'R13', desc: 'Billing Country Code (ISO 3166). E.g. Ireland = [IE], United Kingdom = [UK].', req: 'Y', def: 'AN(3)' },
  { name: 'R14', desc: 'Vendor ID.', req: 'Y', def: 'AN(50)' },
  { name: 'R15', desc: 'Successful URL — redirects the consumer if the transaction is successful.', req: 'Y', def: 'AN(250)' },
  { name: 'R16', desc: 'Non-Successful URL — redirects the consumer if the transaction is not successful.', req: 'Y', def: 'AN(250)' },
  { name: 'R17', desc: 'Originating Merchant URL — only required for PSPs.', req: 'N', def: 'AN(2000)' },
  { name: 'R18', desc: 'Originating Merchant IP — only required for PSPs.', req: 'N', def: 'AN(15)' },
  { name: 'R19', desc: "Consumer's Email Address.", req: 'N', def: 'AN(250)' },
  { name: 'R20', desc: 'Language Code. E.g. English = [EN], French = [FR], Dutch = [NL].', req: 'N', def: 'AN(2)' },
  { name: 'R21', desc: 'Back URL — URL address of the back button displayed in the iFrame.', req: 'N', def: 'AN(2000)' },
  { name: 'udf', desc: 'UDF List — user-defined fields for merchant-supplied metadata.', req: 'N', def: 'AN(20)' },
];

const VALIDATION = [
  { name: 'R1',  desc: 'Transaction Code.',            regex: '^[-_0-9A-Za-z]{0,40}$' },
  { name: 'R3',  desc: 'Numeric Currency Code.',       regex: '^[0-9]{3}$' },
  { name: 'R4',  desc: 'Total Transaction Amount.',    regex: '^[0-9]{0,20}$' },
  { name: 'R5',  desc: 'Billing First Name.',          regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R6',  desc: 'Billing Middle Name.',         regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R7',  desc: 'Billing Last Name.',           regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R8',  desc: 'Billing Address Line 1.',      regex: "^[a-zA-Z0-9\"/\\s,()'#.`-]{1,255}$" },
  { name: 'R9',  desc: 'Billing Address Line 2.',      regex: "^[a-zA-Z0-9\"/\\s,()'#.`-]{0,255}$" },
  { name: 'R10', desc: 'Billing City.',                regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R11', desc: 'Billing State/Province.',      regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R12', desc: 'Billing Postal Code.',         regex: "^[a-zA-Z0-9 '.,-]{1,10}$" },
  { name: 'R13', desc: 'Billing Country Code.',        regex: '^[a-zA-Z]{2,3}$' },
  { name: 'R14', desc: 'Vendor ID.',                   regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R15', desc: 'Successful URL.',              regex: '^(ht|f)tp(s?)://…$' },
  { name: 'R16', desc: 'Non-Successful URL.',          regex: '^(ht|f)tp(s?)://…$' },
  { name: 'R17', desc: 'Originating Merchant URL.',    regex: '^(ht|f)tp(s?)://…$' },
  { name: 'R18', desc: 'Originating Merchant IP.',     regex: '^[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}$' },
  { name: 'R19', desc: "Consumer's Email Address.",    regex: '(^$|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$)' },
  { name: 'R20', desc: 'Language Code.',               regex: 'EN | FR | NL' },
  { name: 'R21', desc: 'Back URL.',                    regex: '^(ht|f)tp(s?)://…$' },
];

const RESP_FIELDS = [
  { name: 'R1',  desc: (<>Error Code (see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link> for a list of available error codes).</>), def: 'AN(50)' },
  { name: 'R2',  desc: 'Description — human-readable message paired with R1.', def: 'AN(50)' },
  { name: 'R3',  desc: "Merchant's Transaction Code.", def: 'AN(50)' },
  { name: 'R4',  desc: 'The Hosted Payments Page URL to complete the transaction (used for the iFrame).', def: 'AN(2048)' },
  { name: 'udf', desc: 'UDF List — any merchant-supplied UDF values returned back to you.', def: 'AN(50)' },
];

/* Local LangTabs removed — now using shared <LangTabs /> from /components/LangTabs.jsx */

/* ----- HPP visual flow ----- */
const ProcessFlow = () => {
  const steps = [
    { n: 1, t: 'Collect & Submit', d: "Collect consumer's billing info and POST it to the eCOMM API using the registerpayment signature." },
    { n: 2, t: 'Load iFrame URL',  d: 'The response returns the Hosted Payments Page URL in R4. Load it inside an iFrame on your site.' },
    { n: 3, t: 'Cardholder Enters Card', d: 'Consumer enters card details on the Hosted Payments Page. 3D Secure is handled automatically.' },
    { n: 4, t: 'Redirect & Notify', d: 'Consumer is redirected to your Success or Failure URL; the final result is POSTed to your Notification URL.' },
  ];
  return (
    <ol className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="hpp-process-flow">
      {steps.map((s, i) => (
        <li
          key={s.n}
          className="relative rounded-lg border-2 border-[#1F4E79]/15 bg-gradient-to-br from-white to-blue-50/40 p-4"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center justify-center w-7 h-7 bg-[#1F4E79] text-white rounded-full text-xs font-bold">
              {s.n}
            </span>
            <h4 className="text-sm font-bold text-gray-900">{s.t}</h4>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">{s.d}</p>
          {i < steps.length - 1 && (
            <span className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 text-[#1F4E79] text-xl font-bold select-none">
              &rsaquo;
            </span>
          )}
        </li>
      ))}
    </ol>
  );
};

const HostedPaymentsPage = () => (
  <ContainerPageLayout
    currentId="hosted-payment-page"
    title="Hosted Payments Page"
    subtitle="An iFrame-embedded checkout hosted by eCOMM — no PCI scope on the merchant, 3D Secure handled automatically, and fully customisable via CSS."
    titleBadge="iFrame"
    signature="registerpayment"
    toc={TOC}
    mainTestId="hosted-payment-page"
    sidebarTestId="hosted-payment-sidebar"
  >
    {/* Overview */}
    <section id="overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Overview</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        This solution allows the merchant to have an embedded iFrame within their website that will load eCOMM's Hosted Payments page. The merchant's developer can choose to embed the iFrame within a checkout page or create a pop-up modal that will contain the iFrame.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The consumer's data is collected on the merchant's website where it is posted to the eCOMM API via our{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">registerpayment</code> API. The response contains the URL which will be loaded into the iFrame. The consumer enters their card details and submits the form; if the card is{' '}
        <Link to="/technical-documentation/api-reference/3d-secure-auth" className="text-[#1F4E79] underline hover:text-[#163a5f]">3D Secure</Link>, they are prompted to authenticate. On success the consumer is redirected to a success page that was pre-determined in the initial <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">registerpayment</code> request. The final transaction result inside the confirmation notification will be sent to the merchant's Notification URL. <strong>Our Hosted Payments Page handles the flow of 3D Secure for you.</strong>
      </p>

      <div className="bg-amber-50/90 border border-amber-200 rounded-lg p-4 flex items-start gap-3 mt-5">
        <Info size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-700">
          <strong>Notification Method must be set to <em>Both</em>:</strong> please provide eCOMM with your desired Notification URL — for Hosted Payments Page the notification method can only be set to <code className="bg-white/70 px-1 rounded">Both</code>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white/70 p-4">
          <ShieldCheck size={20} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-gray-900">Minimal PCI Scope</p>
            <p className="text-xs text-gray-600 mt-1">Card data never touches your servers — entered directly into the iFrame.</p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white/70 p-4">
          <Palette size={20} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-gray-900">Custom CSS Styling</p>
            <p className="text-xs text-gray-600 mt-1">Supply eCOMM with a CSS file and we'll brand the Hosted Payments Page to match your site.</p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white/70 p-4">
          <Globe size={20} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-gray-900">DCC Ready</p>
            <p className="text-xs text-gray-600 mt-1">Supports Dynamic Currency Conversion — supply eCOMM with the currencies you wish to accept.</p>
          </div>
        </div>
      </div>
    </section>

    {/* Process */}
    <section id="process" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Hosted Payments Page Process</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-6">
        The process of the Hosted Payments Page API is simple and straightforward. Below demonstrates the steps required in order to successfully cater for our Hosted Payments Page.
      </p>

      <ProcessFlow />

      <ol className="list-decimal pl-6 space-y-3 text-gray-700 text-sm leading-relaxed mt-6">
        <li>The consumer's information is collected and submitted to our API.</li>
        <li>The response contains the URL required to pass into an iFrame that will load the Hosted Payments Page. Here the consumer must then enter their card details and submit.</li>
        <li>
          If the consumer's card is{' '}
          <Link to="/technical-documentation/api-reference/3d-secure-auth" className="text-[#1F4E79] underline hover:text-[#163a5f]">3D Secure</Link>, they must enter their unique secure code and submit in order to authenticate themselves.
        </li>
        <li>If 3D Secure and the transaction is successful, the consumer is redirected to the designated successful page. If the transaction fails, the consumer is redirected to the designated failure page. These pages are pre-determined in the initial <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">registerpayment</code> request.</li>
        <li>The overall transaction result will be sent to the merchant's Notification URL.</li>
      </ol>
    </section>

    {/* Making The Request */}
    <section id="making-request" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Making The Request</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-3">
        HTTP is used as the request-response protocol between a merchant's site and the eCOMM API. In the back end, a merchant submits a HTTP POST request to the eCOMM server, which then returns an XML document where the merchant must cater for the action listed.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-3">
        The request string that is sent for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Hosted Payments Page</code> call must be composed of the following information:
      </p>
      <ol className="list-decimal pl-6 space-y-1 text-gray-700 text-sm mb-6">
        <li><strong>Username</strong> = SomeName</li>
        <li><strong>Password</strong> = SomePassword</li>
        <li><strong>MessageID</strong> = <em>*GUID (e.g. 30dd879c-ee2f-11db-8314-0800200c9a66)</em></li>
        <li><strong>APISignature</strong> = Registerpayment</li>
        <li><strong>Data</strong> = Form data in XML format</li>
      </ol>

      <div id="sample-request-url" className="scroll-mt-28">
        <h3 className="text-lg font-bold text-gray-900 mb-2">A fully formatted HTTP POST request should look like this:</h3>
        <CodeBlock code={REQUEST_URL_SAMPLE} lang="http" testid="request-url-code" />
      </div>

      <div id="sample-request-code" className="scroll-mt-28 mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-xl font-bold text-gray-900">Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-base font-mono">Hosted Payments Page</code> Request</h3>
          <DownloadButtons />
        </div>
        <LangTabs samples={{ php: PHP_SAMPLE, csharp: CSHARP_SAMPLE }} testidBase="hpp-lang" />
      </div>

      <div id="iframe-embed" className="scroll-mt-28 mt-8">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={16} className="text-[#ef5b25]" />
          <h3 className="text-xl font-bold text-gray-900">iFrame Embed Snippet</h3>
        </div>
        <p className="text-gray-700 text-sm leading-relaxed mb-2">
          Once you have the URL returned in the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">&lt;R4&gt;</code> field of the response, drop it into an iFrame on your checkout page:
        </p>
        <CodeBlock code={IFRAME_SNIPPET} lang="html" testid="iframe-snippet-code" />
      </div>

      <div id="request-reference" className="scroll-mt-28 mt-10">
        <ApiPlayground
          apiSignature="registerpayment"
          title="Request Reference — Hosted Payments Page"
          description="Register a Hosted Payments Page transaction. The response's <R4> field contains the URL to embed in your iFrame."
          defaultData={HPP_XML.replace('<!-- Hosted Payments Page Request Demonstration -->\n', '')}
        />
      </div>
    </section>

    {/* Data Required */}
    <section id="data-required" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Data Required</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        When the Hosted Payments Page is loaded inside an iFrame, the consumer is presented with a card-entry form. They enter their card information and submit — the form redirects to the merchant's success or failure page depending on the result. The merchant also receives a confirmation notification via their Notification URL.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <figure className="rounded-lg border border-gray-200 overflow-hidden bg-white/70">
          <img
            src="https://dev.ecomm365.com/ecommerce/assets/images/Pages/Hosted-Payments-Page/HPP.jpg"
            alt="Sample Hosted Payments Page Form"
            className="w-full h-auto"
            loading="lazy"
          />
          <figcaption className="px-3 py-2 text-xs text-gray-600 border-t border-gray-200 bg-gray-50">
            Standard Hosted Payments Page
          </figcaption>
        </figure>
        <figure className="rounded-lg border border-gray-200 overflow-hidden bg-white/70">
          <img
            src="https://dev.ecomm365.com/ecommerce/assets/images/Pages/Hosted-Payments-Page/HPP-DCC.jpg"
            alt="Sample Hosted Payments Page (DCC) Form"
            className="w-full h-auto"
            loading="lazy"
          />
          <figcaption className="px-3 py-2 text-xs text-gray-600 border-t border-gray-200 bg-gray-50">
            Hosted Payments Page with DCC (Dynamic Currency Conversion)
          </figcaption>
        </figure>
      </div>

      <p className="text-gray-700 text-sm leading-relaxed mt-5">
        While using the same information as the normal Hosted Payments Page process, a quick background check runs when the cardholder enters their Card Number — if DCC is applicable for the entered card, a new section appears below the Card Expiry displaying DCC, and the consumer can choose which currency they wish to proceed with.
      </p>
    </section>

    {/* Form Fields */}
    <section id="available-form-fields" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Available Form Data Fields</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below is a table containing all the available fields for passing POST data into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter within the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Hosted Payments Page</code> request. These fields should be filled using collected data from the consumer on a form level.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="form-fields-table">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3 w-24">Required</th>
              <th className="text-left p-3 w-28">Definition</th>
            </tr>
          </thead>
          <tbody>
            {FORM_FIELDS.map((f, i) => (
              <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                <td className="p-3">{f.desc}</td>
                <td className="p-3"><ReqBadge value={f.req} /></td>
                <td className="p-3 font-mono text-xs">{f.def}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Validation */}
    <section id="validation" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Available Form Data Fields — Validation</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below is a table containing all the available fields for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter within the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Hosted Payments Page</code> request including its validation. These are used when constructing the data.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3">Validation (Regex)</th>
            </tr>
          </thead>
          <tbody>
            {VALIDATION.map((v, i) => (
              <tr key={v.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{v.name}</td>
                <td className="p-3">{v.desc}</td>
                <td className="p-3 font-mono text-xs break-all">{v.regex}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Sample XML */}
    <section id="sample-xml" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
        Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-2xl font-mono">Hosted Payments Page</code> XML Document
      </h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The below sample demonstrates what is expected when passing POST data into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter.
      </p>
      <CodeBlock code={HPP_XML} lang="xml" testid="hpp-xml-code" />
    </section>

    {/* Data Returned */}
    <section id="data-returned" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Data Returned</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">Below are the expected XML fields returned from the request.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3 w-28">Definition</th>
            </tr>
          </thead>
          <tbody>
            {RESP_FIELDS.map((f, i) => (
              <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                <td className="p-3">{f.desc}</td>
                <td className="p-3 font-mono text-xs">{f.def}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Expected Responses */}
    <section id="expected-responses" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Expected XML Documents</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        This section demonstrates the expected XML document responses returned from the HTTP POST once the request has passed validations.
      </p>

      <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-5 mb-6">
        <p className="font-semibold text-[#1F4E79] mb-3">Expected error code responses:</p>
        <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-2">
          <li><strong className="text-[#c32222]">0038</strong> — Successful request. The response includes the URL required to pass into the iFrame in the <code className="bg-white/70 px-1 rounded">&lt;R4&gt;</code> field.</li>
        </ol>
      </div>

      <h3 className="text-lg font-bold text-gray-900 mb-2">Sample XML Response (0038)</h3>
      <CodeBlock code={RESP_0038} lang="xml" testid="resp-0038-code" />

      <h3 className="text-lg font-bold text-gray-900 mb-2 mt-6">Sample XML Response (Confirmation Notification)</h3>
      <p className="text-sm text-gray-600 mb-2">
        The below is a sample of what to expect to be sent to the Notification URL if your notification method is set up to do so. <strong>Card Information</strong> can be included once set at VID level — the merchant must request this to be turned on.
      </p>
      <CodeBlock code={RESP_NOTIF} lang="xml" testid="resp-notif-code" />
    </section>
  </ContainerPageLayout>
);

export default HostedPaymentsPage;
